import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

// Icons
import {
  FiUser,
  FiCpu,
  FiArrowLeft,
  FiExternalLink,
  FiCopy,
  FiCheck,
  FiKey,
} from "react-icons/fi";

// Lightbox
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import { useHookPppoeUserDetail } from "../../../../../hooks/useHookPppoeUserDetail";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import { PaymentSection } from "./sections/PaymentSection";
import { LocationAndImageSection } from "./sections/location_and_images";
import { MdOutlineArrowOutward } from "react-icons/md";
import { FaEdit, FaCheck } from "react-icons/fa";
import { InfrastucturUpdate } from "./sub_feature/infrastuctur_update";
import { PppoeProfileUpdate } from "./sub_feature/profile_update";
import { PppoeOntConfigUpdate } from "./sub_feature/ont_config_update";
import { BtnDefault } from "../../../../../components/buttons/BtnDefault";
import { PppoeServiceUpdate } from "./sub_feature/service_update";

/* ============================================
   Style Constants
   ============================================ */
const CARD_STYLE = {
  background: "rgba(15, 23, 42, 0.5)",
  border: "1px solid rgba(51, 65, 85, 0.3)",
  backdropFilter: "blur(12px)",
};

const CARD_HOVER_STYLE = {
  borderColor: "rgba(0, 180, 216, 0.15)",
  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
};

const HEADER_ICON_THEMES = {
  blue: {
    bg: "rgba(56, 189, 248, 0.12)",
    border: "1px solid rgba(56, 189, 248, 0.15)",
    color: "#38bdf8",
    shadow: "0 4px 12px rgba(56, 189, 248, 0.15)",
  },
  indigo: {
    bg: "rgba(129, 140, 248, 0.12)",
    border: "1px solid rgba(129, 140, 248, 0.15)",
    color: "#818cf8",
    shadow: "0 4px 12px rgba(129, 140, 248, 0.15)",
  },
  emerald: {
    bg: "rgba(52, 211, 153, 0.12)",
    border: "1px solid rgba(52, 211, 153, 0.15)",
    color: "#34d399",
    shadow: "0 4px 12px rgba(52, 211, 153, 0.15)",
  },
  orange: {
    bg: "rgba(251, 146, 60, 0.12)",
    border: "1px solid rgba(251, 146, 60, 0.15)",
    color: "#fb923c",
    shadow: "0 4px 12px rgba(251, 146, 60, 0.15)",
  },
};

const STATUS_THEMES = {
  aktif: {
    bg: "rgba(52, 211, 153, 0.1)",
    border: "1px solid rgba(52, 211, 153, 0.2)",
    color: "#34d399",
    dot: "#34d399",
  },
  nonaktif: {
    bg: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    color: "#f87171",
    dot: "#f87171",
  },
  default: {
    bg: "rgba(100, 116, 139, 0.1)",
    border: "1px solid rgba(100, 116, 139, 0.2)",
    color: "#94a3b8",
    dot: "#94a3b8",
  },
};

/* ============================================
   Utility Helpers
   ============================================ */
const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const getStatusTheme = (status) =>
  STATUS_THEMES[status] || STATUS_THEMES.default;

/* ============================================
   Skeleton Pulse
   ============================================ */
const SkeletonPulse = ({ className }) => (
  <div className={`animate-pulse rounded-lg bg-slate-700/40 ${className}`} />
);

/* ============================================
   KOMPONEN: CopyToast
   ============================================ */
const CopyToast = ({ show, label }) => {
  if (!show) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none">
      <div
        className="flex items-center gap-2.5 px-5 py-3 rounded-xl shadow-2xl shadow-emerald-500/10 border border-emerald-500/20"
        style={{
          background:
            "linear-gradient(135deg, rgba(6, 78, 59, 0.92), rgba(15, 23, 42, 0.95))",
          backdropFilter: "blur(16px)",
          animation: "copyToastIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        }}
      >
        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20">
          <FaCheck className="text-emerald-400 text-xs" />
        </span>
        <span className="text-sm font-medium text-emerald-300 whitespace-nowrap">
          {label} berhasil disalin
        </span>

        <style>{`
          @keyframes copyToastIn {
            0%   { opacity: 0; transform: translateY(16px) scale(0.95); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>
    </div>
  );
};

/* ============================================
   KOMPONEN: StatusBadge
   ============================================ */
const StatusBadge = ({ status }) => {
  const theme = getStatusTheme(status);

  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{
        background: theme.bg,
        border: theme.border,
        color: theme.color,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: theme.dot }}
      />
      {status || "Unknown"}
    </span>
  );
};

/* ============================================
   KOMPONEN: DetailItem
   ============================================ */
const DetailItem = ({
  label,
  value,
  highlight = false,
  onAction = null,
  actionIcon: ActionIcon = null,
  isLoading = false,
  isCopied = false,
}) => (
  <div className="group">
    <label className="text-[9px] uppercase tracking-widest text-slate-500 font-black block mb-1">
      {label}
    </label>
    {isLoading ? (
      <SkeletonPulse className="h-5 w-3/4" />
    ) : (
      <div className="flex items-center justify-between gap-2">
        <span
          className={`text-sm truncate ${highlight
            ? "text-cyan-400 font-bold underline decoration-cyan-400/20 underline-offset-4"
            : "text-slate-200 font-semibold"
            }`}
        >
          {value || "—"}
        </span>
        {onAction && ActionIcon && (
          <button
            onClick={onAction}
            className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${
              isCopied
                ? "text-emerald-400 bg-emerald-400/10"
                : "text-slate-600 hover:text-cyan-400 hover:bg-cyan-400/10"
            }`}
          >
            <ActionIcon size={14} />
          </button>
        )}
      </div>
    )}
  </div>
);

/* ============================================
   KOMPONEN: InfoCard
   ============================================ */
const InfoCard = ({
  title,
  icon: Icon,
  theme = "blue",
  children,
  isLoading = false,
  setOpenModal = false,
}) => {
  const iconTheme = HEADER_ICON_THEMES[theme];

  return (
    <article
      className="rounded-2xl p-5 transition-all duration-300"
      style={CARD_STYLE}
      onMouseEnter={(e) =>
        Object.assign(e.currentTarget.style, CARD_HOVER_STYLE)
      }
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Card Header */}
      <div
        className="flex items-center justify-between gap-3 mb-5 pb-4"
        style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.25)" }}
      >
        {isLoading ? (
          <SkeletonPulse className="w-10 h-10 rounded-xl" />
        ) : (
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              background: iconTheme.bg,
              border: iconTheme.border,
              boxShadow: iconTheme.shadow,
            }}
          >
            <Icon className="text-lg" style={{ color: iconTheme.color }} />
          </div>
        )}

        <h3 className="font-bold text-gray-100 text-sm tracking-tight flex-1">
          {title}
        </h3>

        <button
          onClick={() => setOpenModal(true)}
          className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 bg-amber-600/10 hover:bg-amber-600/20"
        >
          <FaEdit className="text-lg text-amber-500" />
        </button>
      </div>

      {/* Card Body */}
      <div className="space-y-4">{children}</div>
    </article>
  );
};

/* ============================================
   KOMPONEN UTAMA: PppoeUserDetail
   ============================================ */
export const PppoeUserDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showSidebar, setShowSidebar] = useState(false);
  const [openLightbox, setOpenLightbox] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  /* ── Copy state: per-field + label untuk toast ── */
  const [copiedField, setCopiedField] = useState("");
  const [copyLabel, setCopyLabel] = useState("");

  const { data, loading, error, refreshData } = useHookPppoeUserDetail({
    id: id,
  });

  /* ---------- Lightbox Slides ---------- */
  const slides = (data?.images || []).map((img) => ({
    src: img.url || img,
  }));

  /* ---------- Copy Handler ---------- */
  const handleCopy = (text, field, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setCopyLabel(label);
      setTimeout(() => {
        setCopiedField("");
        setCopyLabel("");
      }, 2000);
    });
  };

  /* ---------- Derived Values ---------- */
  const userName = data?.user?.name || "Loading...";
  const pageTitle = loading ? "Detail Pelanggan" : `Detail ${userName}`;

  /* ---------- Modal States ---------- */
  const [openModalUpdateProfile, setOpenModalUpdateProfile] = useState(false);
  const [openModalUpdateService, setOpenModalUpdateService] = useState(false);
  const [openModalUpdateOntConfig, setOpenModalUpdateOntConfig] =
    useState(false);
  const [openModalUpdateInfrastuctur, setOpenModalUpdateInfrastuctur] =
    useState(false);

  return (
    <>
      <Helmet>
        <title>{pageTitle} - DANELA NET</title>
        <meta
          name="description"
          content={`Detail informasi, layanan, dan infrastruktur pelanggan PPPoE ${userName}.`}
        />
      </Helmet>

      {/* Lightbox */}
      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        index={photoIndex}
        slides={slides}
        plugins={[Zoom]}
        zoom={{ maxZoomPixelRatio: 3, zoomInMultiplier: 2 }}
      />

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header
          showToggleSidebar={false}
          toggleSidebar={() => setShowSidebar(false)}
        >
          <div className="flex items-center gap-4 py-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 rounded-xl text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 transition-all flex-shrink-0"
              style={CARD_STYLE}
            >
              <FiArrowLeft />
            </button>
            <div className="min-w-0">
              {loading ? (
                <SkeletonPulse className="h-7 w-56" />
              ) : (
                <h2 className="text-lg md:text-xl font-extrabold text-gray-100 tracking-tight truncate">
                  Detail Pelanggan
                </h2>
              )}
              {!loading && (
                <p className="text-xs text-slate-500 mt-0.5 hidden md:block">
                  Manajemen informasi dan infrastruktur user
                </p>
              )}
            </div>
          </div>

          <button
            onClick={() => navigate(`/dashboard/pppoe/user/edit/${id}`)}
            className="flex items-center gap-2 bg-cyan-600 text-white px-5 py-2.5 rounded-xl text-xs font-bold hover:bg-cyan-500 transition-all shadow-lg shadow-cyan-600/20 flex-shrink-0"
          >
            Edit Data
          </button>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

            {/* ── Card 1: Profil Personal ── */}
            <InfoCard
              title="Profil Personal"
              icon={FiUser}
              theme="blue"
              isLoading={loading}
              setOpenModal={setOpenModalUpdateProfile}
            >
              <DetailItem
                label="No Internet"
                value={data?.user?.internet_number}
                isLoading={loading}
                onAction={() =>
                  handleCopy(
                    data?.user?.internet_number,
                    "no_internet",
                    "No Internet"
                  )
                }
                actionIcon={
                  copiedField === "no_internet" ? FiCheck : FiCopy
                }
                isCopied={copiedField === "no_internet"}
              />
              <DetailItem
                label="Nama Lengkap"
                value={data?.user?.name}
                isLoading={loading}
              />
              <DetailItem
                label="No. Telepon"
                value={data?.user?.phone_number}
                isLoading={loading}
                onAction={() =>
                  handleCopy(
                    data?.user?.phone_number,
                    "phone",
                    "No. Telepon"
                  )
                }
                actionIcon={
                  copiedField === "phone" ? FiCheck : FiCopy
                }
                isCopied={copiedField === "phone"}
              />
              <DetailItem
                label="Ip Address"
                value={data?.user?.ip_address}
                isLoading={loading}
                onAction={() => {
                  if (data?.user?.ip_address) {
                    window.open(
                      `http://${data.user.ip_address}`,
                      "_blank"
                    );
                  }
                }}
                actionIcon={
                  data?.user?.ip_address
                    ? MdOutlineArrowOutward
                    : null
                }
              />
              <DetailItem
                label="Mitra"
                value={data?.mitra?.name}
                isLoading={loading}
              />
              {!loading && (
                <div
                  className="pt-2"
                  style={{
                    borderTop:
                      "1px dashed rgba(51, 65, 85, 0.3)",
                  }}
                >
                  <StatusBadge status={data?.user?.status} />
                </div>
              )}
            </InfoCard>

            {/* ── Card 2: Paket & Layanan ── */}
            <InfoCard
              title="Paket & Layanan"
              icon={FiCpu}
              theme="indigo"
              isLoading={loading}
              setOpenModal={setOpenModalUpdateService}
            >
              <DetailItem
                label="Profile"
                value={data?.service?.profile?.name}
                highlight
                isLoading={loading}
              />
              <DetailItem
                label="Harga"
                value={formatCurrency(
                  data?.service?.profile?.price
                )}
                isLoading={loading}
              />
              <DetailItem
                label="Diskon"
                value={
                  data?.service?.diskon
                    ? formatCurrency(data?.service?.diskon)
                    : null
                }
                isLoading={loading}
              />
              <DetailItem
                label="PPN"
                value={`${data?.service?.ppn}%`}
                isLoading={loading}
              />
              <DetailItem
                label="Tgl Instalasi"
                value={data?.service?.registration_date}
                isLoading={loading}
              />
            </InfoCard>

            {/* ── Card 3: Konfigurasi ONT (semua bisa copy) ── */}
            <InfoCard
              title="Konfigurasi ONT"
              icon={FiKey}
              theme="emerald"
              isLoading={loading}
              setOpenModal={setOpenModalUpdateOntConfig}
            >
              <DetailItem
                label="Username"
                value={data?.ont_configuration?.username}
                isLoading={loading}
                onAction={() =>
                  handleCopy(
                    data?.ont_configuration?.username,
                    "ont_username",
                    "Username"
                  )
                }
                actionIcon={
                  copiedField === "ont_username"
                    ? FiCheck
                    : FiCopy
                }
                isCopied={copiedField === "ont_username"}
              />
              <DetailItem
                label="Password"
                value={data?.ont_configuration?.password}
                isLoading={loading}
                onAction={() =>
                  handleCopy(
                    data?.ont_configuration?.password,
                    "ont_password",
                    "Password"
                  )
                }
                actionIcon={
                  copiedField === "ont_password"
                    ? FiCheck
                    : FiCopy
                }
                isCopied={copiedField === "ont_password"}
              />
              <DetailItem
                label="Nama Wifi"
                value={data?.ont_configuration?.wifi_name}
                isLoading={loading}
                onAction={() =>
                  handleCopy(
                    data?.ont_configuration?.wifi_name,
                    "ont_wifi_name",
                    "Nama Wifi"
                  )
                }
                actionIcon={
                  copiedField === "ont_wifi_name"
                    ? FiCheck
                    : FiCopy
                }
                isCopied={copiedField === "ont_wifi_name"}
              />
              <DetailItem
                label="Password Wifi"
                value={data?.ont_configuration?.wifi_password}
                isLoading={loading}
                onAction={() =>
                  handleCopy(
                    data?.ont_configuration?.wifi_password,
                    "ont_wifi_password",
                    "Password Wifi"
                  )
                }
                actionIcon={
                  copiedField === "ont_wifi_password"
                    ? FiCheck
                    : FiCopy
                }
                isCopied={copiedField === "ont_wifi_password"}
              />

              <BtnDefault
                onClick={() =>
                  navigate(
                    `/dashboard/genie-acs/detail/${data?.serial_number}`
                  )
                }
              >
                Lihat Modem
              </BtnDefault>
            </InfoCard>

            {/* ── Card 4: Infrastruktur ── */}
            <InfoCard
              title="Infrastruktur"
              icon={FiExternalLink}
              theme="orange"
              isLoading={loading}
              setOpenModal={setOpenModalUpdateInfrastuctur}
            >
              <DetailItem
                label="NAS / Core"
                value={data?.infrastuktur?.nas?.name}
                isLoading={loading}
              />
              <DetailItem
                label="Point of Presence (POP)"
                value={data?.infrastuktur?.pop?.name}
                isLoading={loading}
              />
              <DetailItem
                label="ODC"
                value={data?.infrastuktur?.odc?.name}
                isLoading={loading}
              />
              <DetailItem
                label="ODP"
                value={data?.infrastuktur?.odp?.name}
                isLoading={loading}
              />
            </InfoCard>
          </div>

          {/* Section Lokasi & Foto */}
          <LocationAndImageSection
            location={data?.address}
            images={data?.images}
            pppoeUserId={id}
            refreshData={refreshData}
          />

          {/* Riwayat Pembayaran */}
          <PaymentSection
            pppoeUserId={id}
            refreshData={refreshData}
          />
        </main>

        {/* ===== Modals ===== */}
        <PppoeProfileUpdate
          data={data}
          open={openModalUpdateProfile}
          setOpen={setOpenModalUpdateProfile}
          refreshData={refreshData}
        />

        <PppoeServiceUpdate
          data={data}
          open={openModalUpdateService}
          setOpen={setOpenModalUpdateService}
          refreshData={refreshData}
        />

        <PppoeOntConfigUpdate
          data={data}
          open={openModalUpdateOntConfig}
          setOpen={setOpenModalUpdateOntConfig}
          refreshData={refreshData}
        />

        <InfrastucturUpdate
          data={data}
          open={openModalUpdateInfrastuctur}
          setOpen={setOpenModalUpdateInfrastuctur}
          refreshData={refreshData}
        />

        {/* ===== Copy Toast ===== */}
        <CopyToast
          show={!!copiedField}
          label={copyLabel}
        />
      </DashboardLayout>
    </>
  );
};